import React, { useEffect, useRef } from "react";
import { ToastProviderStyle as S } from "./ToastProvider.style";
import { PToast } from "components/prime/PToast/PToast";
import { Toast, ToastMessage } from "primereact/toast";
import { useObservable } from "@rsm";
import { Subject } from "rxjs";
import { AiToast } from "./ToastVariants/AiToast/AiToast";
import { MatIcon } from "components/MatIcon/MatIcon";

export interface CustomToastMessage extends Omit<ToastMessage, "severity"> {
  severity?: "success" | "info" | "warn" | "error" | "secondary" | "contrast" | undefined | "ai";
  isLoadingState?: boolean;
  sticky?: boolean;
  /** Render this message in the centre of the screen instead of the default top-right stack. */
  centered?: boolean;
}

class ToastStore {
  private _request$ = new Subject<{
    action: "show" | "remove" | "replace" | "clear";
    message: CustomToastMessage | CustomToastMessage[];
  }>();

  public request$ = this._request$.asObservable();

  constructor() {}
  public sendMessage = (message: CustomToastMessage | CustomToastMessage[]) =>
    this._request$.next({ message, action: "show" });
  public removeMessage = (message: CustomToastMessage | CustomToastMessage[]) =>
    this._request$.next({ message, action: "remove" });
  public replaceMessage = (message: CustomToastMessage | CustomToastMessage[]) =>
    this._request$.next({ message, action: "replace" });
  public clearMessages = () => this._request$.next({ message: [], action: "clear" });
}

const toastStore = Object.freeze(new ToastStore());

const isCentered = (message: CustomToastMessage | CustomToastMessage[]) =>
  Array.isArray(message) ? message.some((m) => m.centered) : !!message.centered;

export const ToastProvider: React.FC<{}> = () => {
  const ref = useRef<Toast>(null);
  const centeredRef = useRef<Toast>(null);
  const request = useObservable(toastStore.request$);

  useEffect(() => {
    const { message, action } = request || { message: undefined, action: undefined };
    // Centered messages render in a separate top-centre instance so they can't clash with the
    // default top-right stack. `clear` targets both instances.
    const target = message && isCentered(message) ? centeredRef.current : ref.current;
    if (action && target) {
      if (action === "show") {
        target.show(message as ToastMessage);
      } else if (action === "remove") {
        target.remove(message as ToastMessage);
      } else if (action === "replace") {
        target.replace(message as ToastMessage);
      } else if (action === "clear") {
        ref.current?.clear();
        centeredRef.current?.clear();
      }
    }
  }, [request]);

  return (
    <S.ToastProvider>
      <PToast ref={ref as never} />
      <PToast ref={centeredRef as never} position="top-center" />
    </S.ToastProvider>
  );
};

const mapMessage = (message: CustomToastMessage) => {
  const messageWithDefaults = Object.assign(
    { severity: "success", life: 3000, sticky: message.sticky ?? false },
    message,
  );

  switch (messageWithDefaults.severity) {
    case "ai":
      return Object.assign(messageWithDefaults, {
        content: <AiToast {...message} />,
      });
  }

  return messageWithDefaults;
};

export const toast = (message: CustomToastMessage | CustomToastMessage[]) =>
  toastStore.sendMessage(Array.isArray(message) ? message.map((m) => mapMessage(m)) : mapMessage(message));

export const toastSuccess = (summary: string, detail: string) => {
  const message = mapMessage({
    severity: "success",
    summary,
    detail,
    icon: <MatIcon>check_circle</MatIcon>,
  });
  toastStore.sendMessage(message);
  return message;
};
export const toastError = (summary: string, detail: string | React.ReactNode) => {
  const message = mapMessage({
    severity: "error",
    summary,
    detail,
    icon: <MatIcon>cancel</MatIcon>,
  });
  toastStore.sendMessage(message);
  return message;
};
export const toastWarn = (summary: string, detail: string | React.ReactNode) => {
  const message = mapMessage({
    severity: "warn",
    summary,
    detail,
    icon: <MatIcon>error_outline</MatIcon>,
  });
  toastStore.sendMessage(message);
  return message;
};
export const toastInfo = (summary: string, detail: string) => {
  const message = mapMessage({
    severity: "info",
    summary,
    detail,
    icon: <MatIcon>info</MatIcon>,
  });
  toastStore.sendMessage(message);
  return message;
};

export const clearToast = () => toastStore.clearMessages();
export const removeToast = (message: CustomToastMessage | CustomToastMessage[]) =>
  toastStore.removeMessage(Array.isArray(message) ? message.map((m) => mapMessage(m)) : mapMessage(message));
export const replaceToast = (message: CustomToastMessage | CustomToastMessage[]) =>
  toastStore.replaceMessage(Array.isArray(message) ? message.map((m) => mapMessage(m)) : mapMessage(message));
